import React from 'react';
import { Page } from '../../layout';
import PageInstructions from '@shared/components/PageInstructions';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { Navigate, useParams } from 'react-router-dom';
import { ObservationReportUpload } from './ObservationReportUpload';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import { useQuery, useReactiveVar } from '@apollo/client';
import { userInfo } from '@apollo-internal/cache';
import { useManageObservationReportDocuments } from '../hooks/useManageObservationReportDocuments';
import { Button } from '@mui/material';
import ApolloError from '@shared/components/ApolloError';
import { ObservationReportDocumentsMapper } from '../mappers/ObservationReportDocumentsMapper';
import GET_OBSERVATION_REPORT_DOCUMENTS from '../queries/getObservationReportDocumentsQuery';

const PAGE_TITLE = 'Observation Report';
const DESCRIPTION =
  'Upload your Observation report for the individual that you Observed.' +
  ' Once you press the Submit button the MDDAP Team will review.' +
  ' If the Report needs updates it will be returned to you here.';
const SUBMIT_BUTTON_TEXT = 'Submit';

export const ObservationReportSubmissionPage = () => {
  const { eventId, observedUserId } = useParams();
  const currentUserInfo = useReactiveVar(userInfo);
  const parsedEventId = parseInt(eventId);
  const parsedObservedUserId = parseInt(observedUserId);

  const {
    data: observationReportData,
    loading: observationReportDataLoading,
    error: observationReportDataError
  } = useQuery(GET_OBSERVATION_REPORT_DOCUMENTS, {
    fetchPolicy: 'network-only',
    variables: { eventId: parsedEventId, observedUserId: parsedObservedUserId },
    notifyOnNetworkStatusChange: true
  });

  const observationReport = observationReportData?.observationReports
    ? ObservationReportDocumentsMapper.toPageData(
        observationReportData.observationReports,
        observationReportData.observationReportEventOrgWithOrgUnits
      )
    : null;

  const {
    handleAddObservationReport,
    handleDownloadObservationReportFile,
    addObservationReportError,
    uploadFileLoading
  } = useManageObservationReportDocuments(parsedEventId, parsedObservedUserId);

  const breadcrumbPathItems = [{ name: PAGE_TITLE, url: '/' }];

  if (observationReportData && observationReportData.observationReports === null) {
    return <Navigate to={getDataNotFoundPage(currentUserInfo.Tier)} />;
  }

  if (observationReportDataError || addObservationReportError) {
    return <ApolloError />;
  }

  return (
    <Page title={PAGE_TITLE}>
      <Breadcrumb
        loading={observationReportDataLoading}
        pathItems={breadcrumbPathItems}
        annotationItems={[
          observationReport?.orgName,
          observationReport?.orgUnitNames,
          observationReport?.observedUserName
        ]}>
        {!observationReport?.document?.fileName && !observationReportDataLoading && (
          <div className='add-button'>
            <Button
              data-testid='submit-button'
              className='primary-button sm'
              type='submit'
              form='observation-report-form'
              disabled={uploadFileLoading}>
              {SUBMIT_BUTTON_TEXT}
            </Button>
          </div>
        )}
      </Breadcrumb>

      <PageInstructions
        title={PAGE_TITLE}
        description={DESCRIPTION}
        loading={observationReportDataLoading}
      />

      <ObservationReportUpload
        observationReport={observationReport}
        eventId={parsedEventId}
        observedUserId={parsedObservedUserId}
        dataLoading={observationReportDataLoading}
        handleAddObservationReport={handleAddObservationReport}
        handleDownloadObservationReportFile={handleDownloadObservationReportFile}
        uploadFileLoading={uploadFileLoading}
      />
    </Page>
  );
};
